// Body stats (plan 008): the rules behind the numbers, charts and comparisons, kept apart from the
// screens so they can be tested. Design: docs/design/body-stats/v2/SPEC.md ("Chart and comparison rules").

export const METRICS = {
  weight: { key: 'weight', label: 'Weight', unit: 'kg', unitName: 'kilograms', changeUnit: 'kg' },
  fat: { key: 'fat', label: 'Body fat', unit: '%', unitName: 'percent', changeUnit: 'pts' },
  muscle: { key: 'muscle', label: 'Muscle', unit: 'kg', unitName: 'kilograms', changeUnit: 'kg' },
};

export const MUSCLE_KINDS = {
  skeletal_muscle: 'Skeletal muscle mass',
  muscle_mass: 'Muscle mass',
  lean_mass: 'Lean mass',
};

// Machines offered when typing; "Other" asks for a name. The sheet's own name for muscle is suggested.
// The owner could not find their gym's machine on 17 October 2026: the usual gym models are listed, and a
// brand's "other model" for one that is not.
export const COMMON_MACHINES = [
  { name: 'InBody 230', muscleKind: 'skeletal_muscle' },
  { name: 'InBody 270', muscleKind: 'skeletal_muscle' },
  { name: 'InBody 370', muscleKind: 'skeletal_muscle' },
  { name: 'InBody 380', muscleKind: 'skeletal_muscle' },
  { name: 'InBody 570', muscleKind: 'skeletal_muscle' },
  { name: 'InBody 580', muscleKind: 'skeletal_muscle' },
  { name: 'InBody 770', muscleKind: 'skeletal_muscle' },
  { name: 'InBody 970', muscleKind: 'skeletal_muscle' },
  { name: 'InBody (other model)', muscleKind: 'skeletal_muscle' },
  { name: 'Tanita MC-580', muscleKind: 'muscle_mass' },
  { name: 'Tanita MC-780', muscleKind: 'muscle_mass' },
  { name: 'Tanita MC-980', muscleKind: 'muscle_mass' },
  { name: 'Tanita (other model)', muscleKind: 'muscle_mass' },
  { name: 'Boditrax', muscleKind: 'muscle_mass' },
  { name: 'Evolt 360', muscleKind: 'skeletal_muscle' },
  { name: 'Seca mBCA', muscleKind: 'skeletal_muscle' },
  { name: 'Styku', muscleKind: 'lean_mass' },
  { name: 'Withings scale', muscleKind: 'muscle_mass' },
  { name: 'Renpho scale', muscleKind: 'muscle_mass' },
  { name: 'Home scale (other)', muscleKind: 'muscle_mass' },
];
export const suggestedMuscleKind = (machine) =>
  COMMON_MACHINES.find((m) => m.name === machine)?.muscleKind ?? 'muscle_mass';

// Where a weigh-in came from, for the weight chart: a scan's machine, or "Your scale" for every
// other weigh-in (check-ins, the app, sign-up). The line breaks where this changes.
export const SCALE = 'Your scale';
export const weightSourceName = (weighIn, scansByWeighIn = new Map()) => {
  if (weighIn.source !== 'scan') return SCALE;
  const machine = scansByWeighIn.get(weighIn.id)?.machine;
  return machine || (weighIn.notes || '').replace(/^Body scan: /, '') || 'Body scan';
};

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
const valueOf = (scan, metric) => num(metric === 'weight' ? scan.weight_kg : metric === 'fat' ? scan.body_fat_pct : scan.muscle_kg);

/**
 * Points for one metric, oldest first: { date, value, group, id }.
 * weight: every weigh-in (one weight record); fat and muscle: scans, grouped by machine.
 */
export function pointsFor(metric, { scans = [], weighIns = [] }) {
  if (metric === 'weight') {
    const byWeighIn = new Map(scans.filter((s) => s.weight_log_id).map((s) => [s.weight_log_id, s]));
    return weighIns
      .map((w) => ({ id: w.id, date: new Date(w.date ?? w.logged_at), value: num(w.weight ?? w.weight_kg), group: weightSourceName(w, byWeighIn) }))
      .filter((p) => p.value !== null && !Number.isNaN(p.date.getTime()))
      .sort((a, b) => a.date - b.date || String(a.id).localeCompare(String(b.id)));
  }
  return scans
    .map((s) => ({ id: s.id, date: dayOf(s.measured_on), value: valueOf(s, metric), group: s.machine, kind: s.muscle_kind ?? null }))
    .sort((a, b) => a.date - b.date || String(a.id).localeCompare(String(b.id)));
}

// A measured day as a Date at noon UTC (no time zone surprises for a date-only value)
export const dayOf = (isoDay) => new Date(`${String(isoDay).slice(0, 10)}T12:00:00Z`);

/**
 * The latest value and its change from the one before it in the same group (same machine). The one
 * before is the immediately preceding point in that group: if it has no value, the change is
 * "not recorded", never a silent older baseline.
 */
export function latestWithChange(points) {
  const withValue = points.filter((p) => p.value !== null);
  if (withValue.length === 0) return null;
  const latest = withValue[withValue.length - 1];
  const sameGroup = points.filter((p) => p.group === latest.group && p.date <= latest.date && p !== latest);
  const previous = sameGroup.length ? sameGroup[sameGroup.length - 1] : null;
  if (!previous) return { latest, previous: null, change: null, notRecorded: false };
  if (previous.value === null) return { latest, previous, change: null, notRecorded: true };
  return { latest, previous, change: round(latest.value - previous.value), notRecorded: false };
}

export const round = (v, places = 1) => Math.round(v * 10 ** places) / 10 ** places;

// "▼ 0.3 kg" / "▲ 0.1 pts" / "→ 0.0 kg": arrows, never colours
export const formatChange = (change, metric) => {
  if (change === null || change === undefined) return null;
  const arrow = change > 0 ? '▲' : change < 0 ? '▼' : '→';
  return `${arrow} ${Math.abs(change).toFixed(1)} ${METRICS[metric].changeUnit}`;
};

// Calendar months back from the latest point; "all" keeps everything
export const RANGES = [
  { key: '3m', label: '3M', months: 3 },
  { key: '6m', label: '6M', months: 6 },
  { key: '1y', label: '1Y', months: 12 },
  { key: 'all', label: 'All', months: null },
];
export function inRange(points, rangeKey) {
  const range = RANGES.find((r) => r.key === rangeKey);
  if (!range?.months || points.length === 0) return points;
  const end = points[points.length - 1].date;
  const start = new Date(end);
  start.setUTCMonth(start.getUTCMonth() - range.months);
  return points.filter((p) => p.date >= start);
}

/**
 * The lines to draw: one per group (a machine, or "Your scale"), joining that group's readings in date
 * order, broken where a reading is missing. Groups never join each other, even when their readings
 * alternate (weigh-ins from check-ins between gym scans). The dividers mark where a machine is replaced:
 * a group whose readings end before another group's begin, never to return ("Tanita from 3 Sep").
 */
export function segmentsOf(points) {
  const segments = [];
  const groups = [...new Set(points.map((p) => p.group))];
  for (const g of groups) {
    let current = null;
    for (const p of points.filter((q) => q.group === g)) {
      if (p.value === null) { current = null; continue; }
      if (!current) { current = []; segments.push(current); }
      current.push(p);
    }
  }
  const changes = [];
  const lastOf = (g) => Math.max(...points.filter((p) => p.group === g).map((p) => p.date.getTime()));
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]; const p = points[i];
    const isNew = !points.slice(0, i).some((q) => q.group === p.group);
    if (p.group !== prev.group && isNew && lastOf(prev.group) <= p.date.getTime()) changes.push({ date: p.date, group: p.group });
  }
  return { segments, changes, groups };
}

// y limits: half units around the data, never zero span (SPEC rule 3)
export function yLimits(values) {
  const v = values.filter((x) => x !== null);
  if (v.length === 0) return null;
  let lo = Math.floor((Math.min(...v) - 0.3) * 2) / 2;
  let hi = Math.ceil((Math.max(...v) + 0.3) * 2) / 2;
  if (hi - lo < 1) { lo -= 0.5; hi += 0.5; }
  return { lo, hi };
}

/** To minus From for the three numbers, and whether the two scans are on different machines. */
export function compareScans(from, to) {
  if (!from || !to) return null;
  const diff = (m) => {
    const a = valueOf(from, m); const b = valueOf(to, m);
    return a === null || b === null ? null : round(b - a);
  };
  return {
    weight: diff('weight'), fat: diff('fat'), muscle: diff('muscle'),
    differentMachines: from.machine !== to.machine,
    differentMuscleKinds: from.muscle_kind && to.muscle_kind && from.muscle_kind !== to.muscle_kind,
  };
}

/** Typed entry: the numbers within the database's bounds, at least one, muscle with its kind. */
export function validateScan({ measuredOn, machine, weight, fat, muscle, muscleKind }, today = new Date()) {
  const errors = {};
  const todayIso = new Date(today.getTime() - today.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  if (!measuredOn) errors.measuredOn = 'Choose the date of the scan';
  else if (measuredOn > todayIso) errors.measuredOn = 'The date cannot be after today';
  if (!machine || !machine.trim()) errors.machine = 'Choose or name the machine';
  else if (machine.trim().length > 80) errors.machine = 'Keep the machine name under 80 characters';
  const w = num(weight); const f = num(fat); const m = num(muscle);
  if (w !== null && (Number.isNaN(w) || w <= 0 || w > 400)) errors.weight = 'Weight between 1 and 400 kg';
  if (f !== null && (Number.isNaN(f) || f < 2 || f > 75)) errors.fat = 'Body fat between 2 and 75 %';
  if (m !== null && (Number.isNaN(m) || m <= 0 || m > 200)) errors.muscle = 'Muscle between 1 and 200 kg';
  if (m !== null && !errors.muscle && w !== null && !errors.weight && m >= w) errors.muscle = 'Muscle must be less than weight';
  if (m !== null && !muscleKind) errors.muscleKind = 'Choose what the sheet calls it';
  if (w === null && f === null && m === null) errors.form = 'Enter at least one measurement';
  return errors;
}

// ── The four-weekly reminder (owner, 17 October 2026: scans any time, and a reminder every four weeks) ──
// Owner, 18 October 2026: a slim line below the workouts, not in a new client's first week, and after
// three "Not now"s it waits for the next four-weekly time.
export const REMIND_AFTER_DAYS = 28;
export const SNOOZE_DAYS = 7;
export const FIRST_WEEK_DAYS = 7;
export const NOT_NOWS_BEFORE_PAUSE = 3;

const localDay = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);

/**
 * Whether to remind: no scan yet (once the client has been with Cipher a week), or the latest is
 * REMIND_AFTER_DAYS old or more; not while "Not now" is in force. Returns null or { neverScanned, days }.
 */
export function scanReminder({ latestDay, snoozedUntil = null, joinedAt = null, now = new Date() }) {
  if (snoozedUntil && Number(snoozedUntil) > now.getTime()) return null;
  if (!latestDay) {
    const joined = joinedAt ? new Date(joinedAt) : null;
    if (joined && !Number.isNaN(joined.getTime()) && now.getTime() - joined.getTime() < FIRST_WEEK_DAYS * 86400000) return null;
    return { neverScanned: true, days: null };
  }
  // Whole calendar days between the scan's day and today
  const days = Math.round((dayOf(localDay(now)).getTime() - dayOf(latestDay).getTime()) / 86400000);
  return days >= REMIND_AFTER_DAYS ? { neverScanned: false, days } : null;
}

/** What is kept on the device: { until, count }; an older copy kept only the time. */
export function readReminderSnooze(stored) {
  if (!stored) return { until: null, count: 0 };
  try {
    const v = JSON.parse(stored);
    if (typeof v === 'number') return { until: v, count: 0 };
    return { until: Number(v.until) || null, count: Number(v.count) || 0 };
  } catch {
    return { until: null, count: 0 };
  }
}

/** After "Not now": a week, or on the third time running, four weeks (and the count starts again). */
export function afterNotNow(prev, now = new Date()) {
  const count = (prev?.count || 0) + 1;
  return count >= NOT_NOWS_BEFORE_PAUSE
    ? { until: now.getTime() + REMIND_AFTER_DAYS * 86400000, count: 0 }
    : { until: now.getTime() + SNOOZE_DAYS * 86400000, count };
}
